import { useEffect, useState, type ReactNode } from "react";
import type { BookingSummary, BookingSummaryRow } from "@/types";
import type { FichaMetric } from "./metrics";
import { AppointmentList } from "./AppointmentList";
import { MetricGrid } from "./MetricGrid";
import { PeriodChips } from "./PeriodChips";
import { RankingList } from "./RankingList";
import { Cadence, SparklineChart } from "./SparklineChart";

export function FichaBody({
  analytics,
  timezone,
  metrics,
  hrefFor,
  atRisk = false,
  extra,
}: {
  analytics: BookingSummary;
  timezone: string;
  metrics: FichaMetric[];
  hrefFor: (opts: { period?: string; pastPage?: number }) => string;
  atRisk?: boolean;
  extra?: ReactNode;
}) {
  const [pastRows, setPastRows] = useState<BookingSummaryRow[]>(analytics.past);
  const [pastPage, setPastPage] = useState(analytics.past_pagination?.page ?? 1);
  const [hasMore, setHasMore] = useState(analytics.past_pagination?.has_more === true);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    setPastRows(analytics.past);
    setPastPage(analytics.past_pagination?.page ?? 1);
    setHasMore(analytics.past_pagination?.has_more === true);
  }, [analytics.period, analytics.past_pagination?.total, analytics.past[0]?.id]);

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const response = await fetch(hrefFor({ pastPage: pastPage + 1 }), {
        headers: { Accept: "application/json" },
      });
      if (!response.ok) return;
      const json = await response.json();
      const next = json.analytics as BookingSummary;
      setPastRows((rows) => [...rows, ...next.past]);
      setPastPage(next.past_pagination?.page ?? pastPage + 1);
      setHasMore(next.past_pagination?.has_more === true);
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div className="space-y-4">
      {atRisk && (
        <div className="rounded-xl border border-border bg-yellow-500/10 px-4 py-3 text-sm text-yellow-500">
          Se está yendo. La última visita duplica su recurrencia.
        </div>
      )}

      <div className="rounded-xl border border-border bg-card p-4 sm:p-6">
        <div className="mb-4">
          <PeriodChips
            period={analytics.period}
            hrefFor={(period) => hrefFor({ period, pastPage: 1 })}
          />
        </div>
        <MetricGrid metrics={metrics} />
        <SparklineChart
          values={analytics.sparkline}
          grain={analytics.period === "90d" ? "week" : "month"}
        />
        <Cadence gaps={analytics.cadence_days} />
      </div>

      <div className="grid gap-4 lg:grid-cols-[1.15fr_0.85fr]">
        <div className="space-y-4">
          <AppointmentList
            title="Próximas"
            rows={analytics.upcoming}
            timezone={timezone}
            empty="Todavía no tiene citas agendadas."
          />
          <AppointmentList
            title="Pasadas"
            rows={pastRows}
            timezone={timezone}
            empty="Sin historial en este periodo."
            hasMore={hasMore}
            loadingMore={loadingMore}
            onLoadMore={loadMore}
          />
        </div>
        <div className="space-y-4">
          <RankingList
            title="Servicios"
            items={analytics.rankings.services}
            currency={analytics.currency}
          />
          <RankingList
            title="Equipo"
            items={analytics.rankings.staff}
            currency={analytics.currency}
          />
          <RankingList
            title="Clientes"
            items={analytics.rankings.clients}
            currency={analytics.currency}
          />
          <RankingList
            title="Espacios"
            items={analytics.rankings.spaces}
            currency={analytics.currency}
          />
          <RankingList
            title="Origen"
            items={analytics.rankings.sources}
            currency={analytics.currency}
          />
          {extra}
        </div>
      </div>
    </div>
  );
}
